import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faSackDollar,
  faHandHoldingDollar,
  faScaleBalanced,
  faMagnifyingGlass,
  faXmark,
  faUserGraduate,
  faTriangleExclamation,
  faRotate,
  faMoneyBillWave,
  faClock,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import 'flag-icons/css/flag-icons.min.css';
import { countryOf, ianaOf, localTimeIn } from './Studentzone';
import './MyAdminStudents.css';

const API = process.env.REACT_APP_API_BASE_URL;

const money = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

const leftOf = (s) => (Number(s.totalSessions) || 0) - (Number(s.sessionsTaken) || 0);

const SORTS = {
  left: { label: 'Fewest sessions left', fn: (a, b) => leftOf(a) - leftOf(b) },
  name: { label: 'Name A–Z', fn: (a, b) => `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`) },
};

/* Student photo, with an icon when there's none or it fails to load */
const Avatar = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="rmamy-avatar is-empty" aria-hidden="true">
        <FontAwesomeIcon icon={faUserGraduate} />
      </span>
    );
  }
  return (
    <span className="rmamy-avatar">
      <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
    </span>
  );
};

/* Same props as before: { userData } */
const MyAdminStudents = ({ userData }) => {
  const [params, setParams] = useSearchParams();
  const activeTab = params.get('tab') === 'inactive' ? 'inactive' : 'active';
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | ending | unpaid
  const [level, setLevel] = useState('all'); // all | little | junior
  const [sort, setSort] = useState('left');
  const [now, setNow] = useState(() => new Date());

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!userData) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setError('');
    (async () => {
      try {
        const response = await axios.get(`${API}/api/admin/mystudents`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        });
        if (!cancelled) setStudents(response.data?.students || []);
      } catch (err) {
        console.error('Error fetching students:', err);
        if (!cancelled) setError('We couldn’t load the students.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData, reload]);

  // students' local clocks
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const setTab = (tab) => {
    const next = new URLSearchParams(params);
    if (tab === 'active') next.delete('tab');
    else next.set('tab', tab);
    setParams(next, { replace: true });
    setFilter('all');
  };

  // active = sessions left; inactive = none left (also catches students who took MORE than paid)
  const activeStudents = useMemo(() => students.filter((s) => leftOf(s) > 0), [students]);
  const inactiveStudents = useMemo(() => students.filter((s) => leftOf(s) <= 0), [students]);

  // ---------- Totals (active students, same maths as before) ----------
  const upcomingSessionsTotal = activeStudents.reduce((sum, s) => sum + Math.max(0, leftOf(s)), 0);
  const upcomingRevenueTotal = activeStudents.reduce((sum, s) => sum + Math.max(0, leftOf(s)) * (Number(s.SessionFees) || 0), 0);
  const upcomingTeacherPayoutTotal = activeStudents.reduce((sum, s) => sum + Math.max(0, leftOf(s)) * (Number(s.SessionRate) || 0), 0);
  const margin = upcomingRevenueTotal - upcomingTeacherPayoutTotal;

  // Little / Junior (shown once the server sends IdGradesPlans)
  const hasLevel = students.some((s) => s.IdGradesPlans !== undefined);
  const isLittle = (s) => Number(s.IdGradesPlans) === 1;
  const tabPool = activeTab === 'active' ? activeStudents : inactiveStudents;
  const littleCount = tabPool.filter(isLittle).length;
  const pool = !hasLevel || level === 'all' ? tabPool : tabPool.filter((s) => (level === 'little' ? isLittle(s) : !isLittle(s)));
  const endingCount = pool.filter((s) => leftOf(s) <= 2).length;
  const unpaidCount = pool.filter((s) => Number(s.AwaitingPayment) > 0).length;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pool
      .filter((s) => !q || `${s.FirstName || ''} ${s.LastName || ''}`.toLowerCase().includes(q))
      .filter((s) => {
        if (filter === 'ending') return leftOf(s) <= 2;
        if (filter === 'unpaid') return Number(s.AwaitingPayment) > 0;
        return true;
      })
      .sort(activeTab === 'active' ? SORTS[sort].fn : SORTS.name.fn);
  }, [pool, query, filter, sort, activeTab]);

  // ---------- States ----------
  if (isLoading || !userData) {
    return (
      <div className="rmamy" aria-busy="true">
        <div className="rmamy-stats">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rmamy-skel rmamy-skel--stat" />
          ))}
        </div>
        <div className="rmamy-skel rmamy-skel--bar" />
        <div className="rmamy-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rmamy-skel" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rmamy">
      <header className="rmamy-header">
        <h1>Students</h1>
        <p>Paid students, sessions left and what’s still to come.</p>
      </header>

      {error ? (
        <div className="rmamy-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmamy-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      ) : (
        <>
          {/* ---------- Totals (active students) ---------- */}
          <section className="rmamy-stats" aria-label="Upcoming, from active students">
            <div className="rmamy-stat is-info">
              <span className="rmamy-stat-icon">
                <FontAwesomeIcon icon={faCalendarCheck} />
              </span>
              <span>
                <small>Upcoming sessions</small>
                <strong>{upcomingSessionsTotal}</strong>
              </span>
            </div>
            <div className="rmamy-stat is-good">
              <span className="rmamy-stat-icon">
                <FontAwesomeIcon icon={faSackDollar} />
              </span>
              <span>
                <small>Upcoming revenue (parents)</small>
                <strong>{money(upcomingRevenueTotal)}</strong>
              </span>
            </div>
            <div className="rmamy-stat is-bad">
              <span className="rmamy-stat-icon">
                <FontAwesomeIcon icon={faHandHoldingDollar} />
              </span>
              <span>
                <small>Teacher payout (upcoming)</small>
                <strong>{money(upcomingTeacherPayoutTotal)}</strong>
              </span>
            </div>
            <div className="rmamy-stat is-gold">
              <span className="rmamy-stat-icon">
                <FontAwesomeIcon icon={faScaleBalanced} />
              </span>
              <span>
                <small>Left for RoboMind</small>
                <strong>{money(margin)}</strong>
              </span>
            </div>
          </section>

          {/* ---------- Tabs + tools ---------- */}
          <div className="rmamy-tabs" role="tablist" aria-label="Students">
            <button type="button" role="tab" aria-selected={activeTab === 'active'} className={activeTab === 'active' ? 'is-on' : ''} onClick={() => setTab('active')}>
              Active <span>{activeStudents.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={activeTab === 'inactive'} className={activeTab === 'inactive' ? 'is-on' : ''} onClick={() => setTab('inactive')}>
              Inactive <span>{inactiveStudents.length}</span>
            </button>
          </div>

          <div className="rmamy-tools">
            <label className="rmamy-search">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <input type="search" placeholder="Search by name" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search students" />
              {query && (
                <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </label>
            <div className="rmamy-chips">
              <button type="button" className={filter === 'all' ? 'is-on' : ''} onClick={() => setFilter('all')}>
                All
              </button>
              {activeTab === 'active' && (
                <button type="button" className={`is-warn ${filter === 'ending' ? 'is-on' : ''}`} onClick={() => setFilter('ending')}>
                  1–2 left <b>{endingCount}</b>
                </button>
              )}
              <button type="button" className={`is-bad ${filter === 'unpaid' ? 'is-on' : ''}`} onClick={() => setFilter('unpaid')}>
                Awaiting payment <b>{unpaidCount}</b>
              </button>
            </div>
            {hasLevel && (
              <div className="rmamy-chips" role="group" aria-label="Level">
                <button type="button" className={level === 'all' ? 'is-on' : ''} onClick={() => setLevel('all')}>
                  All levels
                </button>
                <button type="button" className={level === 'little' ? 'is-on' : ''} onClick={() => setLevel('little')}>
                  Little <b>{littleCount}</b>
                </button>
                <button type="button" className={level === 'junior' ? 'is-on' : ''} onClick={() => setLevel('junior')}>
                  Junior <b>{tabPool.length - littleCount}</b>
                </button>
              </div>
            )}
            {activeTab === 'active' && (
              <label className="rmamy-sort">
                <span>Sort</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  {Object.entries(SORTS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {/* ---------- Cards ---------- */}
          {shown.length === 0 ? (
            <div className="rmamy-empty">
              <FontAwesomeIcon icon={faUserGraduate} />
              <p>
                {query || filter !== 'all' || level !== 'all'
                  ? 'No students match.'
                  : activeTab === 'active'
                  ? 'No active students right now.'
                  : 'No inactive students — everyone has sessions left.'}
              </p>
            </div>
          ) : (
            <ul className="rmamy-grid">
              {shown.map((s) => {
                const left = leftOf(s);
                const total = Number(s.totalSessions) || 0;
                const taken = Number(s.sessionsTaken) || 0;
                const pct = total ? Math.min(100, Math.round((taken / total) * 100)) : 0;
                const tone = activeTab === 'inactive' ? 'is-muted' : left === 1 ? 'is-bad' : left === 2 ? 'is-warn' : 'is-info';
                const flag = countryOf(s);
                const tz = ianaOf(s.timezone || s.Timezone || '');
                const clock = tz ? localTimeIn(tz, null, now) : '';
                const unpaid = Number(s.AwaitingPayment) > 0;
                const name = `${s.FirstName || ''} ${s.LastName || ''}`.trim();
                return (
                  <li key={s.IdUser}>
                    <a
                      className={`rmamy-card ${tone} ${activeTab === 'inactive' ? 'is-inactive' : ''}`}
                      href={`/admin/StudentProfile/${s.IdUser}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open profile in a new tab"
                    >
                      <span className="rmamy-photo">
                        <Avatar src={s.Image} alt={name} />
                        {flag && (
                          <span className="rmamy-flag" title={s.timezone || s.Timezone || ''}>
                            <span className={`fi fi-${flag}`} />
                          </span>
                        )}
                      </span>
                      <span className="rmamy-info">
                        <strong className="rmamy-name">{name || 'Student'}</strong>
                        <span className="rmamy-meta">
                          {s.Grade !== undefined && s.Grade !== null && s.Grade !== '' && <>Grade {s.Grade}</>}
                          {s.Age ? <> · Age {s.Age}</> : null}
                          {clock && (
                            <>
                              {' '}
                              · <FontAwesomeIcon icon={faClock} /> {clock}
                            </>
                          )}
                        </span>
                        <span className="rmamy-tags">
                          {activeTab === 'active' ? (
                            <span className="rmamy-pill">
                              {left} {left === 1 ? 'session' : 'sessions'} left
                            </span>
                          ) : (
                            <span className="rmamy-pill">{left < 0 ? `${-left} over paid sessions` : 'Finished'}</span>
                          )}
                          {unpaid && (
                            <span className="rmamy-pill is-unpaid">
                              <FontAwesomeIcon icon={faMoneyBillWave} /> Awaiting payment
                            </span>
                          )}
                        </span>
                        {total > 0 && (
                          <span className="rmamy-bar" aria-label={`${taken} of ${total} sessions taken`}>
                            <i style={{ width: `${pct}%` }} />
                          </span>
                        )}
                        {total > 0 && (
                          <span className="rmamy-count">
                            {taken} / {total} taken
                          </span>
                        )}
                      </span>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="rmamy-open" />
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default MyAdminStudents;